/**
 * @export
 * @class Posts
 */
import * as Flickity from 'flickity';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import * as imagesLoaded from 'imagesloaded';

import { BaseModule } from '../../helpers/base_module';

gsap.registerPlugin(ScrollTrigger);

export class Posts extends BaseModule {
  public static id = '*';

  constructor() {
    super();

    this.carousel();
    this.video();
    this.singleCarousel();
    this.pageAnimations();
  }

  private pageAnimations(): void {
    const rightPage = document.querySelector<HTMLElement>('.single__title');

    if (rightPage) {
      const tl = gsap.timeline();

      tl.add('start')
        .to('.single__subTitle', { opacity: 1, x: 0, duration: 1 }, 'start')
        .to('.single__title', { opacity: 1, x: 0, duration: 1 }, 'start')
        .to('.single__hero', { opacity: 1, duration: 1 }, 'start');

      const pageAnimation = gsap.timeline({
        scrollTrigger: {
          trigger: '.js-page',
          start: 'top 80%',
          end: `bottom bottom`,
        },
      });

      pageAnimation
        .add('animations')
        .to(
          '.js-animatein',
          {
            opacity: 1,
            x: 0,
            duration: 1,
            stagger: {
              each: 0.4,
            },
          },
          'animations'
        )
        .to(
          '.js-page__element',
          {
            opacity: 1,
            x: 0,
            duration: 1,
            stagger: {
              each: 0.4,
            },
          },
          'animations'
        );
    }
  }
  /**
   * Toggle the shipping address block
   *
   * @private
   * @return void
   */
  private singleCarousel(): void {
    const singleGalleries: NodeListOf<Element> = document.querySelectorAll('.js-single__gallery__carousel');
    if (singleGalleries.length) {
      imagesLoaded(singleGalleries, function() {
        for (const singleGallery of singleGalleries as any) {
          new Flickity(singleGallery, {
            pageDots: false,
            prevNextButtons: false,
            imagesLoaded: true,
          });
        }
      });

      const circle = document.querySelector<HTMLElement>('.js-single__gallery__icon');
      const galleryWindow = document.querySelector<HTMLElement>('.js-single__gallery');

      if (circle !== null && galleryWindow !== null) {
        const mouseMoveHandler = (event: MouseEvent): void => {
          circle.style.left = `${event.clientX - circle.offsetWidth / 2}px`;
          circle.style.top = `${event.clientY - circle.offsetHeight / 2}px`;
          circle.classList.add('is-active');
        };
        const mouseOutHandler = (): void => {
          circle.style.left = `0px`;
          circle.style.top = `0px`;
          circle.classList.remove('is-active');
        };

        galleryWindow.addEventListener('mousemove', mouseMoveHandler);
        galleryWindow.addEventListener('mouseout', mouseOutHandler);
      }
    }
  }

  /**
   * Toggle the shipping address block
   *
   * @private
   * @return void
   */
  private video(): void {
    const videoButtons: NodeListOf<Element> = document.querySelectorAll('.js-single__video__button');
    if (videoButtons.length) {
      for (const videoButton of videoButtons as any) {
        videoButton.addEventListener('click', (event: Event) => {
          event.preventDefault();
          videoButton.classList.toggle('is-active');
          const video = videoButton.parentNode.querySelector('.js-video');

          video.play();
        });
      }
    }
  }

  /**
   * Create a carousel
   *
   * @private
   * @return void
   */
  private carousel(): void {
    const carouselContainer = document.querySelector<HTMLElement>('.js-blog__content');
    const carouselContainerArray = document.querySelectorAll<HTMLElement>('.js-blog__item');
    if (carouselContainer) {
      let screenWidth: number = window.innerWidth;
      const carouselAnimation = gsap.timeline();
      let blockWidth: number = carouselContainerArray[0].offsetWidth;
      let frameSize: number = blockWidth * (carouselContainerArray.length - 1);
      imagesLoaded(carouselContainer, function() {
        const flkty = new Flickity(carouselContainer, {
          freeScroll: true,
          prevNextButtons: false,
          pageDots: false,
          draggable: true,
          freeScrollFriction: 0.1,
          cellAlign: 'left',
        });

        window.addEventListener('resize', () => {
          flkty.resize();
          screenWidth = window.innerWidth;
          animateList(screenWidth);
          blockWidth = carouselContainerArray[0].offsetWidth;
          frameSize = blockWidth * (carouselContainerArray.length - 1);
          ScrollTrigger.refresh();
        });

        const animateList = function(animateMobileWidth: number) {
          if (animateMobileWidth > 780 && carouselContainerArray.length > 3) {
            carouselAnimation.to('.flickity-slider', {
              x: `-${frameSize}`,
              ease: 'none',
              scrollTrigger: {
                trigger: '.js-blog__container',
                start: 'top top',
                end: `${frameSize} top`,
                scrub: true,
                markers: false,
                pin: true,
              },
            });
          } else {
            const allTriggers = ScrollTrigger.getAll();
            for (const trigger of allTriggers) {
              trigger.kill(true);
            }
            carouselAnimation.kill();
            gsap.set(carouselAnimation, { clearProps: true });
          }
        };

        animateList(screenWidth);

        gsap.to('.blog__item', {
          opacity: 1,
          y: 0,
          duration: 1,
          stagger: {
            each: 0.4,
          },
        });

        const tl = gsap.timeline();
        tl.to('.blog__title', { opacity: 1, x: 0, duration: 1 }).to('.blog__subtitle', { opacity: 1, x: 0, duration: 1 }, '-=.5');

        ScrollTrigger.refresh();
      });
    }
  }
}
